import { useState, useRef, useCallback, useEffect } from 'react';
import type { PerformanceState, SyncMessage } from '../types/sync';

/** Connection status of the sync session. */
export interface SyncSessionState {
  status: 'disconnected' | 'connecting' | 'connected' | 'error';
  role: 'leader' | 'follower' | null;
  error: string | null;
}

/** Actions available for managing a sync session. */
export interface SyncSessionActions {
  endSession: () => void;
  broadcastState: (state: PerformanceState) => void;
}

const INITIAL_STATE: SyncSessionState = {
  status: 'disconnected',
  role: null,
  error: null,
};

const MAX_RECONNECT_RETRIES = 3;
const BASE_RECONNECT_DELAY_MS = 1000;
const PING_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes — keeps API Gateway from dropping idle connections

/**
 * Hook that manages the WebSocket connection lifecycle for synchronized
 * performance sessions. Connects automatically when a setlistId is provided.
 * Role (leader/follower) is assigned by the server — first connector becomes
 * leader, subsequent connectors become followers.
 *
 * @param setlistId The setlist ID to connect to. Connection opens automatically when provided.
 * @param onStateReceived Called when a PerformanceState update arrives from the leader.
 */
export function useSyncSession(
  setlistId: string | undefined,
  onStateReceived: (state: PerformanceState) => void,
): { state: SyncSessionState; actions: SyncSessionActions } {
  const [state, setState] = useState<SyncSessionState>(INITIAL_STATE);

  const wsRef = useRef<WebSocket | null>(null);
  const intentionalCloseRef = useRef(false);
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const setlistIdRef = useRef(setlistId);
  setlistIdRef.current = setlistId;

  // Stable refs to avoid stale closures inside WebSocket callbacks.
  const onStateReceivedRef = useRef(onStateReceived);
  onStateReceivedRef.current = onStateReceived;
  const stateRef = useRef(state);
  stateRef.current = state;

  const openWebSocketRef = useRef<(id: string) => void>(() => {});

  /** Stop the keepalive ping timer. */
  const stopPing = useCallback(() => {
    if (pingTimerRef.current) {
      clearInterval(pingTimerRef.current);
      pingTimerRef.current = null;
    }
  }, []);

  /** Start a keepalive ping that sends a lightweight message every 5 minutes. */
  const startPing = useCallback((id: string) => {
    stopPing();
    pingTimerRef.current = setInterval(() => {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
          action: 'sendMessage',
          type: 'ping',
          setlistId: id,
        }));
      }
    }, PING_INTERVAL_MS);
  }, [stopPing]);

  /** Handle an incoming WebSocket message. */
  const handleMessage = useCallback((event: MessageEvent) => {
    try {
      const message = JSON.parse(event.data) as SyncMessage;

      switch (message.type) {
        case 'roleAssigned':
          setState((prev) => ({
            ...prev,
            status: 'connected',
            role: message.role ?? null,
          }));
          break;

        case 'stateUpdate':
        case 'currentState':
          if (message.payload) {
            onStateReceivedRef.current(message.payload);
          }
          break;

        case 'leaderDisconnected':
          setState((prev) => ({
            ...prev,
            error: 'Leader disconnected',
          }));
          break;

        case 'sessionEnded':
          intentionalCloseRef.current = true;
          wsRef.current?.close();
          wsRef.current = null;
          setState(INITIAL_STATE);
          break;
      }
    } catch {
      // Ignore malformed messages.
    }
  }, []);

  /** Attempt to reconnect with exponential backoff (1s, 2s, 4s). */
  const scheduleReconnect = useCallback((id: string) => {
    if (reconnectAttemptsRef.current >= MAX_RECONNECT_RETRIES) {
      setState((prev) => ({
        ...prev,
        status: 'error',
        error: 'Connection lost. Max reconnection attempts reached.',
      }));
      return;
    }

    const delay = BASE_RECONNECT_DELAY_MS * Math.pow(2, reconnectAttemptsRef.current);
    reconnectAttemptsRef.current += 1;

    reconnectTimerRef.current = setTimeout(() => {
      openWebSocketRef.current(id);
    }, delay);
  }, []);

  /** Open a WebSocket connection for the given setlist. */
  const openWebSocket = useCallback(
    (id: string) => {
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }

      const wsUrl =
        import.meta.env.VITE_WEBSOCKET_URL ||
        'wss://gnjo4pvmje.execute-api.us-east-1.amazonaws.com/prod';
      const url = `${wsUrl}?setlistId=${id}`;

      intentionalCloseRef.current = false;
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        reconnectAttemptsRef.current = 0;
        startPing(id);
        // Request role assignment from the server
        ws.send(JSON.stringify({
          action: 'sendMessage',
          type: 'getRole',
          setlistId: id,
        }));
      };

      ws.onmessage = handleMessage;

      ws.onclose = () => {
        stopPing();
        if (!intentionalCloseRef.current) {
          setState((prev) => ({ ...prev, status: 'connecting' }));
          scheduleReconnect(id);
        }
      };

      ws.onerror = () => {
        stopPing();
        setState((prev) => ({
          ...prev,
          status: 'error',
          error: 'WebSocket connection error',
        }));
      };
    },
    [handleMessage, scheduleReconnect, startPing, stopPing],
  );

  openWebSocketRef.current = openWebSocket;

  // Auto-connect when setlistId is provided
  useEffect(() => {
    if (!setlistId) {
      setState(INITIAL_STATE);
      return;
    }

    intentionalCloseRef.current = false;
    reconnectAttemptsRef.current = 0;
    setState({ status: 'connecting', role: null, error: null });
    openWebSocket(setlistId);

    return () => {
      // Cleanup on unmount or setlistId change
      stopPing();
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }

      const current = stateRef.current;

      // If leader, notify followers before closing
      if (
        current.role === 'leader' &&
        wsRef.current &&
        wsRef.current.readyState === WebSocket.OPEN &&
        setlistIdRef.current
      ) {
        const message: SyncMessage = {
          action: 'sendMessage',
          type: 'endSession',
          setlistId: setlistIdRef.current,
        };
        wsRef.current.send(JSON.stringify(message));
      }

      intentionalCloseRef.current = true;
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [setlistId, openWebSocket]);

  // ── Session Actions ──────────────────────────────────────────────────

  const broadcastState = useCallback((performanceState: PerformanceState) => {
    const current = stateRef.current;
    if (
      current.role !== 'leader' ||
      current.status !== 'connected' ||
      !wsRef.current ||
      wsRef.current.readyState !== WebSocket.OPEN ||
      !setlistIdRef.current
    ) {
      return;
    }

    const message: SyncMessage = {
      action: 'sendMessage',
      type: 'stateUpdate',
      payload: performanceState,
      setlistId: setlistIdRef.current,
    };

    wsRef.current.send(JSON.stringify(message));
  }, []);

  const endSession = useCallback(() => {
    intentionalCloseRef.current = true;
    stopPing();

    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }

    const current = stateRef.current;

    if (
      current.role === 'leader' &&
      wsRef.current &&
      wsRef.current.readyState === WebSocket.OPEN &&
      setlistIdRef.current
    ) {
      const message: SyncMessage = {
        action: 'sendMessage',
        type: 'endSession',
        setlistId: setlistIdRef.current,
      };
      wsRef.current.send(JSON.stringify(message));
    }

    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    setState(INITIAL_STATE);
  }, [stopPing]);

  return {
    state,
    actions: { endSession, broadcastState },
  };
}
